if (!customElements.get("swiper-slider")) {
  class SwiperSlider extends HTMLElement {
    constructor() {
      super();
      this.slideContainer = this.querySelector("[data-swiper]:not([data-swiper-thumbs])");
      this.slides = this.querySelectorAll("[data-swiper-slide]");
      this.sliderOptions = this.applyDefaultOptions(this.getSliderOptions());
      this.count = 0;
      this.activeIndex = 0;
      this.initialized = false;
      this.boundInitializeSwiper = this.initializeSwiper.bind(this);
      this.boundHandleCylindoLoadedForZoom = this.handleCylindoLoadedForZoom.bind(this);
      this.boundHandleCylindoZoomChange = this.handleCylindoZoomChange.bind(this);
      this.boundUpdateZoomDrivenTouchMoveState = this.updateZoomDrivenTouchMoveState.bind(this);
      this.boundHandleCylindoFrameTrigger = this.handleCylindoFrameTrigger.bind(this);
      this.zoomListenersInitialized = false;
      this.zoomViewers = new Set();
      this.cylindoViewerZoomLevels = new WeakMap();
      this.lastCylindoDisplayAt = 0;
      this.currentCylindoFrameIndex = 0;
      this.skipNextCylindoBoundarySync = false;
      if (this.dataset.swiperThumbs) {
        this.thumbnails = this.querySelector('[data-swiper-thumbs]');
      }
      if (this.dataset.customEvent) {
        this.attachCustomListeners(this.dataset.customEvent);
      }
      if (this.slideContainer && this.slides.length > 0) {
        document.addEventListener(
          "DOMContentLoaded",
          this.initializeSwiper.bind(this)
        );
        document.addEventListener(
          "shopify:section:load",
          this.initializeSwiper.bind(this)
        );
        document.addEventListener(
          "oddit-pagination-complete",
          this.initializeSwiper.bind(this)
        );
        window.addEventListener("resize", this.handleResize.bind(this));
        this.addEventListener("go-to-slide", this.goToSlide.bind(this));
      } else {
        console.warn("SwiperSlider - Slider or Slides Not Found");
      }
    }
    attachCustomListeners(customEvent) {
      switch (customEvent) {
        case "swiper_event":
          document.addEventListener("swiper_event", this.updateSlides.bind(this));
          break;
        case "reinitialize":
          this.addEventListener('reinitialize-swiper', this.boundInitializeSwiper);
          break;
        case "td-image-zoom":
          this.setupZoomListeners();
          
          break;
      }
    }
    setupZoomListeners() {
      if (this.zoomListenersInitialized) return;
      this.zoomListenersInitialized = true;

      this.addEventListener('td-image-zoomed', this.boundUpdateZoomDrivenTouchMoveState);
      this.addEventListener('td-image-zoom-end', this.boundUpdateZoomDrivenTouchMoveState);
      document.addEventListener('td:cylindo-loaded', this.boundHandleCylindoLoadedForZoom);
      this.handleCylindoLoadedForZoom({});
    }

    handleCylindoLoadedForZoom(event) {
      const viewerFromEvent = event?.detail?.viewer;
      if (viewerFromEvent && this.contains(viewerFromEvent)) {
        this.registerCylindoZoomViewer(viewerFromEvent);
      }

      this.querySelectorAll('cylindo-viewer').forEach((viewer) => {
        this.registerCylindoZoomViewer(viewer);
      });

      this.updateZoomDrivenTouchMoveState();
    }

    registerCylindoZoomViewer(viewer) {
      if (!viewer || this.zoomViewers.has(viewer)) return;
      this.zoomViewers.add(viewer);
      this.cylindoViewerZoomLevels.set(viewer, 1);
      viewer.addEventListener('zoom-change', this.boundHandleCylindoZoomChange);
    }

    handleCylindoZoomChange(e) {
      const viewer = e?.currentTarget;
      if (viewer) {
        this.cylindoViewerZoomLevels.set(viewer, this.extractCylindoZoomLevel(e?.detail));
      }

      setTimeout(() => {
        this.updateZoomDrivenTouchMoveState();
      }, 0);
    }

    extractCylindoZoomLevel(detail) {
      if (typeof detail === 'number') return detail;
      if (!detail || typeof detail !== 'object') return 1;

      const candidateKeys = ['zoom', 'scale', 'level', 'zoomLevel'];
      for (const key of candidateKeys) {
        const value = Number(detail[key]);
        if (Number.isFinite(value)) return value;
      }

      return 1;
    }

    getActiveSlideElement() {
      if (!this.slider || !this.slider.slides) return null;
      return this.slider.slides[this.slider.activeIndex] || null;
    }

    isActiveSlideLifestyleZoomed() {
      const activeSlide = this.getActiveSlideElement();
      if (!activeSlide) return false;
      return Boolean(activeSlide.querySelector('td-image-zoom.td-image-zoomed'));
    }

    isActiveSlideCylindoZoomed() {
      const activeSlide = this.getActiveSlideElement();
      if (!activeSlide) return false;

      for (const viewer of this.zoomViewers) {
        const zoomLevel = this.cylindoViewerZoomLevels.get(viewer) || 1;
        if (zoomLevel > 1 && activeSlide.contains(viewer)) {
          return true;
        }
      }

      return false;
    }

    updateZoomDrivenTouchMoveState() {
      if (!this.slider) return;
      if (!this.dataset.hasOwnProperty('productImagesSlider')) return;

      if (!this.isMobileViewport()) {
        this.slider.allowTouchMove = false;
        return;
      }

      const shouldDisableTouchMove = this.isActiveSlideLifestyleZoomed() || this.isActiveSlideCylindoZoomed();
      this.slider.allowTouchMove = !shouldDisableTouchMove;
    }
    resetViewerZoomStates() {
      if (!this.dataset.hasOwnProperty('productImagesSlider')) return;

      const imageZoomElements = this.querySelectorAll('td-image-zoom');
      imageZoomElements.forEach((zoomElement) => {
        if (typeof zoomElement.resetZoom === 'function') {
          zoomElement.resetZoom();
        }
      });

      const viewerElements = this.querySelectorAll('cylindo-viewer');
      viewerElements.forEach((viewer) => {
        if (!Object.prototype.hasOwnProperty.call(viewer, 'zoom') && typeof viewer.zoom === 'undefined') return;
        const currentZoom = Number(viewer.zoom);
        if (Number.isFinite(currentZoom) && currentZoom > 1) {
          viewer.zoom = 1;
        }
      });
    }

    disableSwiper() {
      if (!this.slider) return;
      this.slider.allowTouchMove = false;
    }
    
    enableSwiper() {
      if (!this.slider) return;
      this.slider.allowTouchMove = true;
    }
    updateSlides(event) {
    }
    applyDefaultOptions(options) {
      if (options.loop === undefined) {
        options.loop = true;
      }
      options.speed = 400;
      return options;
    }

    getSliderOptions() {
      try {
        return JSON.parse(this.dataset.swiperOptions);
      } catch (err) {
        console.log(err);
        return {};
      }
    }

    initializeSwiper() {
      if (window.Swiper && !this.initialized) {
        this.initialized = true;
        if(this.thumbnails) {
          this.setThumbnails()
        }
        this.slider = new Swiper(this.slideContainer, this.sliderOptions);
        this.initializeSwiperListeners()
        this.initializeSliderEventListeners();
        this.updateZoomDrivenTouchMoveState();
      } else if (!window.Swiper) {
        console.warn("SwiperSlider - Swiper.js not found");
      }
    }

    handleResize(e) {
      if (this.slider) {
        setTimeout(() => {
          this.slider.updateSize();
        }, 300);
      }
    }

    goToSlide(evt){
      let index = evt.detail.index;
      if(!index && evt.detail.position) {
        const elm = this.querySelector("[data-position='" + evt.detail.position + "']");
        index = [...elm.parentNode.children].indexOf(elm);
      }
      if(index != 0 && !index) return;
      this.slider.slideTo(index);
    }

setThumbnails() {
      const thumbsSlideContainer = this.thumbnails;
      const thumbOptions = JSON.parse(this.thumbnails.dataset.swiperOptions);
      const thumbnailsSlider = new Swiper(thumbsSlideContainer, thumbOptions);
      this.sliderOptions.thumbs = { swiper: thumbnailsSlider };
      this.sliderOptions.on = {
        slideChange: function () {
          let activeIndex = this.activeIndex;
          document.dispatchEvent(
            new CustomEvent("thumbnail-changed", {
              detail: {
                slide: this.slides[this.activeIndex],
              },
            })
          );
          setTimeout(() => {
            
          this.thumbs.swiper.slideTo(activeIndex > 0 ? activeIndex : 0);
          }, 100)
        },
      };
    }
    initializeSwiperListeners() {
      if (this.classList.contains('product-gallery')) {
        this.slider.on('slideChange', () => {
          window.VWO = window.VWO || [];
          window.VWO.event = window.VWO.event || function () {window.VWO.push(["event"].concat([].slice.call(arguments)))};
          window.VWO.event("pdpCarouselClick");
          this.resetViewerZoomStates();
          this.handleCylindoBoundarySlideChange();
          this.updateZoomDrivenTouchMoveState();
        });
      }

      if (this.dataset.hasOwnProperty('cylindoSwiperMobile')) {
        const stepAlignedMainSlider = this.hasLinkedCylindoMainSteps();
        const mobileThumbSlides = [...this.querySelectorAll('.swiper-wrapper > .swiper-slide')];

        mobileThumbSlides.forEach((slide, slideIndex) => slide.addEventListener('click', () => {
          if (slide.matches('td-cylindo-frame-trigger') || slide.querySelector('td-cylindo-frame-trigger')) return;

          const frameCount = this.querySelectorAll('td-cylindo-frame-trigger[data-iteration]').length;
          const newIndex = stepAlignedMainSlider
            ? slideIndex
            : (frameCount > 0 ? Math.max(1, slideIndex - frameCount + 1) : slideIndex + 1);

          document.dispatchEvent(new CustomEvent("td:custom-thumbnail-change", {
            detail: {
              newIndex,
              viewport: this.isMobileViewport() ? 'mobile' : 'desktop',
            },
          }))
        }))
      }

      if (this.dataset.hasOwnProperty('productLifestyleSlider')) {
        this.slides.forEach((slide, slideIndex) => {
          slide.addEventListener('click', () => {
            document.dispatchEvent(new CustomEvent('td:gallery-changed', {detail: {slide: slide}}));
          })
        })
      }
      
      if (this.dataset.hasOwnProperty('productGalleryV3')) {
        document.addEventListener('td:gallery-changed', this.handleGalleryChange.bind(this))
      }
    }

    initializeSliderEventListeners() {
      if (this.dataset.hasOwnProperty('productImagesSlider')) {
        document.addEventListener('appBasedImageChange', this.handleVariantSelected.bind(this))
        if (this.isCylindoMainGallery()) {
          document.addEventListener('td:cylindo-frame-trigger', this.boundHandleCylindoFrameTrigger);
        }
      }
      if (this.classList.contains('product-gallery')) {
        document.addEventListener('td:display-avaatar', this.handleAvaatarDisplay.bind(this))
        document.addEventListener('td:display-cylindo', this.handleCylindoDisplay.bind(this))
        document.addEventListener('td:custom-thumbnail-change', this.handleCustomThumbnail.bind(this))
      }      
    }
    handleVariantSelected(e) {
      const variant = e.detail.variant
      if (variant && variant.featured_image && variant.featured_image.src) {
        this.maybeSlideToImage(variant.featured_image.src)
       
      }
    }
    handleGalleryChange({detail}) {
      console.log("Handling gallery change", detail)
      const matchingSlideIndex = [...this.slides].findIndex((slide) => slide.dataset.mediaId == detail.slide.dataset.mediaId)
      if (matchingSlideIndex > -1) {
        this.slider.slideTo(matchingSlideIndex)
      }
    }
    maybeSlideToImage(url) {
      const cleanedUrl = this.handleFileName(url)
      const matchingSlideIndex = [...this.slides].findIndex((slide) => {
        if (slide.classList.contains('swiper-slide-duplicate')) return
        const image = slide.querySelector('img')
        if (image) {
          const fileName = this.handleFileName(image.src)
          return fileName == cleanedUrl
        }
        return false
      })
      if (matchingSlideIndex >= 0) {
        // NOTE looks like swiper is 1 based indexing --> NOT ANYMORE
        this.slider.slideTo(matchingSlideIndex)
        
      }
    }
    handleAvaatarDisplay() {
      // NOTE wasn't sure a better way around this -> slide to second slide after first render
      if (this.count < 2) {
        this.count = this.count + 1;
        this.slider.slideTo(0);
        return
      } else {this.slider.slideTo(1);}
    }
    handleCylindoDisplay() {
      if (!this.slider) return;
      if (this.offsetParent === null) return;

      const now = Date.now();
      if (now - this.lastCylindoDisplayAt < 200) return;
      this.lastCylindoDisplayAt = now;
      this.currentCylindoFrameIndex = 0;

      if (this.slider.activeIndex !== 0) {
        this.slider.slideTo(0);
      }
    }
    handleCustomThumbnail({detail}) {
      if (this.dataset.hasOwnProperty('cylindoSwiperMobile')) return;
      if (!detail || typeof detail.newIndex !== 'number') return;
      if (!this.isActiveForCurrentViewport()) return;
      if (detail.viewport && detail.viewport !== (this.isMobileViewport() ? 'mobile' : 'desktop')) return;
      this.slider.slideTo(detail.newIndex)
    }
    isActiveForCurrentViewport() {
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      const isDesktopOnly = Boolean(this.closest('.mobile--hide'));
      const isMobileOnly = Boolean(this.closest('.desktop--hide'));

      if (isDesktopOnly) return isDesktop;
      if (isMobileOnly) return !isDesktop;
      return this.offsetParent !== null;
    }
    isCylindoMainGallery() {
      if (this.dataset.hasOwnProperty('cylindoMainSteps')) return true;
      if (!this.dataset.hasOwnProperty('productImagesSlider')) return false;
      return [...this.slides].some((slide) => (
        slide.querySelector('[data-cylindo-wrapper-mount], td-cylindo-wrapper')
      ));
    }
    hasLinkedCylindoMainSteps() {
      const scope = this.closest('.product__media__wrapper') || document;
      return Boolean(scope.querySelector('[data-product-images-slider][data-cylindo-main-steps]'));
    }
    getCylindoFrameTriggers() {
      const scope = this.closest('.product__media__wrapper') || document;
      return [...scope.querySelectorAll('[data-cylindo-swiper-mobile] td-cylindo-frame-trigger[data-iteration]')]
        .sort((a, b) => {
          const aIteration = parseInt(a.dataset.iteration || '0', 10);
          const bIteration = parseInt(b.dataset.iteration || '0', 10);
          return aIteration - bIteration;
        });
    }
    getCylindoFrameSequence() {
      const triggers = this.getCylindoFrameTriggers();
      if (!triggers.length) return [];

      const currentFrames = triggers.map((trigger) => `${trigger.currentFrame || ''}`);
      const hasReliableCurrentFrames = currentFrames.every(Boolean) &&
        (new Set(currentFrames).size === currentFrames.length);

      if (hasReliableCurrentFrames) return currentFrames;
      return triggers.map((trigger) => `${trigger.dataset.frame || ''}`).filter(Boolean);
    }
    isMobileViewport() {
      return window.matchMedia('(max-width: 1023px)').matches;
    }
    handleCylindoFrameTrigger({detail}) {
      const selectedFrame = `${detail?.frame ?? ''}`;
      if (!selectedFrame) return;

      const frames = this.getCylindoFrameSequence();
      const newFrameIndex = frames.findIndex((frame) => frame === selectedFrame);
      if (newFrameIndex > -1) {
        this.currentCylindoFrameIndex = newFrameIndex;
      }
    }
    dispatchCylindoFrame(frame) {
      if (!frame) return;
      document.dispatchEvent(new CustomEvent('td:cylindo-frame-trigger', {
        bubbles: true,
        detail: { frame },
      }));
    }
    triggerCylindoFrameStep(frameIndex, frameValue) {
      const frameTriggers = this.getCylindoFrameTriggers();
      const frameTrigger = frameTriggers[frameIndex];

      if (frameTrigger && typeof frameTrigger.click === 'function') {
        frameTrigger.click();
        return;
      }

      this.dispatchCylindoFrame(frameValue);
    }
    handleCylindoBoundarySlideChange() {
      if (this.dataset.hasOwnProperty('cylindoMainSteps')) return;
      if (!this.slider || !this.isCylindoMainGallery() || !this.isMobileViewport()) return;

      if (this.skipNextCylindoBoundarySync) {
        this.skipNextCylindoBoundarySync = false;
        return;
      }

      const previousIndex = this.slider.previousIndex;
      const activeIndex = this.slider.activeIndex;
      const frames = this.getCylindoFrameSequence();
      const lastFrameIndex = frames.length - 1;

      if (lastFrameIndex < 1) return;

      if (previousIndex === 0 && activeIndex === 1 && this.currentCylindoFrameIndex < lastFrameIndex) {
        const nextFrameIndex = Math.min(this.currentCylindoFrameIndex + 1, lastFrameIndex);
        this.currentCylindoFrameIndex = nextFrameIndex;
        this.skipNextCylindoBoundarySync = true;
        this.slider.slideTo(0, 0);
        this.triggerCylindoFrameStep(nextFrameIndex, frames[nextFrameIndex]);
        return;
      }

      if (previousIndex === 1 && activeIndex === 0) {
        this.currentCylindoFrameIndex = lastFrameIndex;
        this.triggerCylindoFrameStep(lastFrameIndex, frames[lastFrameIndex]);
      }
    }
    handleFileName(url) {
      // console.log(url)
      let filename = url.substring(url.lastIndexOf("/") + 1);

      if (filename.includes('?')) {
        // "your-image_150x.jpg?v=110101010" => "your-image_150x.jpg"
        filename = filename.substr(0, filename.indexOf("?"));
      }
      
      // "your-image_150x.jpg" => "your-image.jpg"
      return filename.replace(/_\d+x\./, ".");
    }
  }
  customElements.define("swiper-slider", SwiperSlider);
  
}
